import type { components } from '@connected/api-client/api.generated';

import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Image } from '@connected/ui/components/image';
import { Separator } from '@connected/ui/components/separator';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { Breadcrumb } from '@/components/breadcrumb';
import { Module } from '@/components/module';

export const Route = createFileRoute('/_auth/customer/locations/$locationId/')({
    component: RouteComponent,
    validateSearch: z.object({
        noProjects: z.boolean().optional(),
    }),
    beforeLoad: async ({ context, params }) => {
        try {
            const object = await context.queryClient.ensureQueryData(
                portaalQueryOptions.portal.objects.view({
                    ObjectId: params.locationId,
                }),
            );

            if (!object.Object) {
                throw notFound();
            }
        }
        catch {
            throw notFound();
        }
    },
});

export type Object = components['schemas']['ViewObjectPortalQueryResultObject'];
type Module = NonNullable<NonNullable<Object['Modules']>[number]>;

function RouteComponent() {
    const params = Route.useParams();
    const { t } = useTranslation('customer-locations');
    const search = Route.useSearch();
    const navigate = useNavigate();

    // Show toast error if no projects are found for this module
    useEffect(() => {
        if (search.noProjects) {
            toast.error(t('locations.no-projects-found'));
            navigate({
                to: '/customer/locations/$locationId',
                params: { locationId: params.locationId },
                search: {},
                replace: true,
            });
        }
    }, [search.noProjects, t, navigate, params.locationId]);

    const { data, isLoading } = useQuery(portaalQueryOptions.portal.objects.view({
        ObjectId: params.locationId,
    }));

    const objectData = useMemo(
        () => (!data || isLoading) ? { Object: null } : data,
        [data, isLoading],
    );

    return (
        <div className="max-w-screen-2xl flex flex-col flex-1 p-8 pb-0 overflow-y-auto h-full">
            <Breadcrumb
                items={[{
                    isActive: true,
                    name: objectData?.Object?.Name || t('locations.projects.title'),
                    to: '/customer/locations/$locationId',
                    params: { locationId: params.locationId },
                }]}
            />
            <div className="mb-6">
                <div className="flex max-sm:flex-wrap gap-1 md:gap-4 justify-start">
                    <h1 className="text-2xl font-semibold">
                        {
                            objectData.Object?.Name || t('locations.projects.title')
                        }
                    </h1>
                </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-6 mb-10">
                <div className="w-full sm:w-1/2 lg:w-2/3 order-2 sm:order-1 flex flex-col gap-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                        {transformModulesWithPreviews(objectData?.Object?.Modules)?.map(module => (
                            <div key={module.Kind}>
                                <Module
                                    isPreview={module.isPreview}
                                    observations={(module.Issues ?? []).map(issue => ({
                                        label: module.Kind && module.Kind?.toLowerCase()
                                            ? t(`locations.modules.${module.Kind?.toLowerCase()}.observations.${issue.Kind ?? ''}`)
                                            : '',
                                        color: issue.HexColorCode ?? '',
                                        children: issue.Count ?? 0,
                                    }))}
                                    objectId={params.locationId}
                                    moduleType={module.Kind?.toLowerCase() ?? ''}
                                    className="p-4"
                                >
                                    <Module.Icon className="border-2 rounded-lg w-12 h-12 flex items-center justify-center">
                                        <img
                                            alt={`/logo-${module.Kind?.toLowerCase()}.svg`}
                                            src={`/logo-${module.Kind?.toLowerCase()}.svg`}
                                            className="w-8 h-8"
                                        />
                                    </Module.Icon>
                                    <Module.Title className="my-2 p-1">
                                        {t(
                                            `locations.modules.${module.Kind?.toLowerCase()}.title`,
                                        )}
                                    </Module.Title>
                                    <Module.Observations
                                        className="space-x-0"
                                        observations={
                                            module.Issues?.length
                                                ? module.Issues
                                                        .filter(issue => issue.Count !== -1)
                                                        .map(issue => ({
                                                            label: module.Kind
                                                                ? t(`locations.modules.${module.Kind.toLowerCase()}.observations.${issue.Kind ?? ''}`)
                                                                : '',
                                                            color: issue.HexColorCode ?? '',
                                                            children: issue.Count ?? 0,
                                                        }))
                                                : (
                                                        module.isPreview
                                                            ? [{ label: '', color: '', isPreview: true, children: 'Preview' }]
                                                            : []
                                                    )

                                        }
                                    />
                                </Module>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="w-full sm:w-1/2 lg:w-1/3 order-1 sm:order-2 border rounded-lg overflow-hidden">
                    <div>
                        <Image
                            className="h-80 w-full object-cover rounded-t-lg border"
                            alt="Object Image"
                            queryOptions={portaalQueryOptions.objects.download({
                                ObjectId: objectData.Object?.ObjectId ?? '',
                            })}
                            enabled={!!objectData.Object?.HasImage}
                        />
                    </div>
                    <div className="p-4 flex flex-col">
                        <div className="flex justify-between mt-2">
                            <p className="text-sm font-semibold mb-1">
                                {t('locations.object.object.name')}
                            </p>
                            <p className="text-sm">{objectData.Object?.Name}</p>
                        </div>
                        <Separator className="my-4" />
                        <div className="flex justify-between">
                            <p className="text-sm font-semibold mb-1">
                                {t('locations.object.object.bag-id')}
                            </p>
                            <p className="text-sm">{objectData.Object?.CadastralRegistrationIdentificationNumber}</p>
                        </div>
                        <Separator className="my-4" />
                        <div className="flex justify-between">
                            <p className="text-sm font-semibold mb-1">
                                {t('locations.object.object.street-house-number')}
                            </p>
                            <p className="text-sm">
                                {objectData.Object?.Street}
                                {' '}
                                {objectData.Object?.StreetNumber}
                            </p>
                        </div>
                        <Separator className="my-4" />
                        <div className="flex justify-between">
                            <p className="text-sm font-semibold mb-1">
                                {t('locations.object.object.postal-code-city')}
                            </p>
                            <p className="text-sm">
                                {objectData.Object?.PostalCode}
                                {' '}
                                {objectData.Object?.Locality}
                            </p>
                        </div>
                        <Separator className="my-4" />
                        <div className="flex justify-between">
                            <p className="text-sm font-semibold mb-1">
                                {t('locations.object.object.gross-area')}
                            </p>
                            <p className="text-sm">{objectData.Object?.SurfaceArea}</p>
                        </div>
                        <Separator className="my-4" />
                        <div className="flex justify-between">
                            <p className="text-sm font-semibold mb-1">
                                {t('locations.object.object.construction-year')}
                            </p>
                            <p className="text-sm">{objectData.Object?.ConstructionYear}</p>
                        </div>
                        <Separator className="my-4" />
                        <div className="flex justify-between">
                            <p className="text-sm font-semibold mb-1">
                                {t('locations.object.object.general_email')}
                            </p>
                            <p className="text-sm">{objectData.Object?.Email}</p>
                        </div>
                        <Separator className="my-4" />
                        <div className="flex justify-between">
                            <p className="text-sm font-semibold mb-1">
                                {t('locations.object.object.general_phone')}
                            </p>
                            <p className="text-sm">{objectData.Object?.TelephoneNumber}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

type ModuleType = {
    Kind?: 'Electrical' | null | undefined;
    Issues?: {
        Kind?: 'ElectricalProjectObservationSevere' | 'ElectricalProjectObservationSerious' | 'ElectricalProjectObservationMinor' | 'ElectricalProjectObservationRemark' | 'ElectricalTicket' | 'ElectricalRequest' | null | undefined;
        Count?: number | null | undefined;
        HexColorCode?: string | null | undefined;
    }[] | null | undefined;
}[] | null | undefined;

function transformModulesWithPreviews(modules: ModuleType): (Module & { isPreview: boolean })[] {
    const allModules = [
        'Electrical',
        'Atex',
        // 'Solar',
        'Air',
        'Water',
        'Legionella',
        // 'Cooling',
    ] as const;

    return allModules.map((moduleKind) => {
        const foundModule = modules?.find(m => m.Kind === moduleKind);
        if (foundModule) {
            return {
                ...foundModule,
                isPreview: false,
            } as Module & { isPreview: boolean };
        }
        return {
            Kind: moduleKind as any,
            Issues: [],
            isPreview: true,
        } as Module & { isPreview: boolean };
    });
}
